import { makeStyles } from '@material-ui/core';

const useRiderManagementStyles = makeStyles((theme: any) => ({
    container: (props: any) => ({}),
    tableContainer: {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        // Columns shrink to their base widths and then scroll, rather than
        // squashing below a readable size on narrow viewports.
        overflowX: 'auto',
        paddingRight: '8px',
        minHeight: 'calc(100vh - 385px)',
        maxHeight: 'calc(100vh - 385px)',
        '&::-webkit-scrollbar': {
            width: '8px',
        },
        '&::-webkit-scrollbar-track': {
            background: '#f1f1f1',
            borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb': {
            background: '#888',
            borderRadius: '8px',
        },
        '&::-webkit-scrollbar-thumb:hover': {
            background: '#555',
        },
    },
    filterContainer: {
        marginBottom: 12,
        padding: '12px 12px',
    },
    // --- Toolbar -----------------------------------------------------------
    toolbar: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 8,
        padding: '10px 12px',
        borderRadius: 10,
        backgroundColor: '#fafbff',
        border: '1px solid #ebedf8',
    },
    searchField: {
        width: 300,
        '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            backgroundColor: '#fff',
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 0',
            fontSize: 14,
            fontWeight: 600,
        },
    },
    searchButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 14,
        borderRadius: 8,
        padding: '6px 18px',
        backgroundColor: '#43419e',
        color: '#fff',
        boxShadow: 'none',
        '&:hover': { backgroundColor: '#37357f' },
        '&.Mui-disabled': { backgroundColor: '#d5d7e8', color: '#fff' },
    },
    ghostButton: {
        textTransform: 'none',
        fontWeight: 700,
        fontSize: 14,
        borderRadius: 8,
        padding: '6px 12px',
        color: '#515f6d',
        '&:hover': { backgroundColor: '#eceef8' },
    },
    toolbarDivider: {
        width: 1,
        height: 26,
        backgroundColor: '#e0e2f0',
        margin: '0 4px',
    },
    filterLabel: {
        fontSize: 13,
        fontWeight: 700,
        color: '#8a92a6',
        whiteSpace: 'nowrap',
    },
    // A filter with one option is the caller's scope, shown read-only rather than as a
    // single-item dropdown. Distinct from an applied filter chip, which is removable.
    scopeBadge: {
        background: '#eef0fb',
        color: '#43419e',
        fontWeight: 600,
        fontSize: 12,
    },
    resultSummary: {
        fontSize: 13,
        fontWeight: 700,
        color: '#515f6d',
        whiteSpace: 'nowrap',
    },
    filterBox: {
        width: 'auto',
        height: 'auto',
        minWidth: 130,
        minHeight: 88,
        boxShadow: 'none',
        padding: '16px 16px',
        borderRadius: '8px',
        backgroundColor: '#EBEDF8',
        cursor: 'pointer',
        justifyContent: 'center',
        alignItems: 'center',
        textAlign: 'center',
        transition: 'background-color 0.2s ease-in-out',
        '&:hover': {
            backgroundColor: '#d8ddf5',
        },
    },
    filterBoxActive: {
        backgroundColor: '#d1d8ff',
        boxShadow: 'inset 0 0 0 2px #43419e',
        '&:hover': {
            backgroundColor: '#c4cdff',
        },
    },
    filterBoxNoMargin: {
        marginRight: 0,
    },
    cardHeader: {
        fontSize: '16px',
        fontWeight: 600,
        flex: 1,
    },
    cardSubHeader: {
        fontSize: '22px',
        fontWeight: 700,
        marginTop: '8px',
    },
    // Single-line by design: the grid has to fit eight columns on one screen,
    // and CellText hands the full value to a tooltip when it clips.
    tableValue: {
        fontWeight: 600,
        fontSize: 14,
        color: '#14112D',
    },
    tableRow: {
        transition: 'background-color 0.15s ease-in-out',
        '&:hover': {
            backgroundColor: '#f7f8ff',
        },
    },
    actionButton: {
        borderRadius: 8,
        padding: 6,
        color: '#43419e',
    },
    actionButtonDanger: {
        borderRadius: 8,
        padding: 6,
        color: '#d94a4a',
    },
    emptyState: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        minHeight: 220,
    },
    emptyTitle: {
        fontSize: 16,
        fontWeight: 700,
        color: '#14112d',
    },
    emptySubtitle: {
        fontSize: 14,
        fontWeight: 600,
        color: '#8a92a6',
    },
    // Base pill for the categorical columns. Each of vendor/city/zone/hub then
    // layers on its own colour below so the four read apart at a glance —
    // colour is a scanning aid only, the header still carries the meaning.
    valueChip: {
        display: 'inline-block',
        fontSize: 13,
        fontWeight: 700,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        borderRadius: 8,
        padding: '2px 10px',
        lineHeight: '18px',
    },
    chipVendor: {
        backgroundColor: '#EEF0FF',
        border: '1px solid #43419E',
        color: '#3A38A0',
    },
    chipCity: {
        backgroundColor: '#E4F5F2',
        border: '1px solid #12857A',
        color: '#0F6F66',
    },
    chipZone: {
        backgroundColor: '#FFF3E0',
        border: '1px solid #C77700',
        color: '#9A5D00',
    },
    chipHub: {
        backgroundColor: '#FBEBF7',
        border: '1px solid #A5348F',
        color: '#8C2779',
    },
    chipPhone: {
        backgroundColor: '#EDF1F7',
        border: '1px solid #4A5A73',
        color: '#3B4A60',
        // Tabular figures so digits line up column-to-column.
        fontVariantNumeric: 'tabular-nums',
    },
    chipEmpId: {
        backgroundColor: '#E8F5EC',
        border: '1px solid #17804B',
        color: '#0F6438',
        fontVariantNumeric: 'tabular-nums',
    },
    chipEmpty: {
        fontSize: 14,
        fontWeight: 600,
        color: '#a4abb8',
    },
    tableSubValue: {
        wordBreak: 'break-all',
        fontWeight: 400,
        fontSize: 14,
        marginTop: '6px',
        color: '#14112D',
    },
    chip: {
        fontSize: 12,
        width: 'auto',
        color: '#fff',
        border: '1px solid #fff',
        borderRadius: '8px',
        padding: '2px 12px',
        textAlign: 'center',
        backgroundColor: 'black',
    },
    chipText: {
        fontSize: 12,
        color: 'inherit',
    },
    kycPending: {
        backgroundColor: '#FEF3C7 !important',
        border: '1px solid #F59E0B !important',
        color: '#D97706 !important',
    },
    kycCompleted: {
        backgroundColor: '#D1FAE5 !important',
        border: '1px solid #10B981 !important',
        color: '#059669 !important',
    },
    statusActive: {
        backgroundColor: '#D1FAE5 !important',
        border: '1px solid #10B981 !important',
        color: '#059669 !important',
    },
    statusInactive: {
        backgroundColor: '#FEE2E2 !important',
        border: '1px solid #EF4444 !important',
        color: '#DC2626 !important',
    },
    paginationContainer: {},
    horizontalCardContents: {
        display: 'flex',
        flexDirection: 'row',
        //flexWrap: 'wrap',
        justifyContent: 'flex-start',
    },
    filterCardContainer: {
        marginBottom: 20,
        padding: '16px 16px',
    },
    sparkLineContainer: {
        overflowY: 'hidden',
        overflowX: 'hidden',
        whiteSpace: 'nowrap',
        paddingBottom: '4px',
        paddingLeft: '16px',
        paddingRight: '16px',
        marginLeft: '-16px',
        marginRight: '-16px',
    },
    smallValueBox: {
        padding: '6px 6px',
        borderRadius: '4px',
        backgroundColor: '#fff',
        width: 'auto',
        height: 'auto',
        minWidth: 56,
        minHeight: 56,
    },
    cardSubHeaderH: {
        fontSize: '22px',
        fontWeight: 700,
        marginLeft: '24px',
    },
}));

export default useRiderManagementStyles;
